/**
 * Password-manager opt-out attributes for connector secret fields.
 *
 * Connector credential/secret inputs render as `type="password"`, so password
 * managers (1Password, LastPass, …) detect them as login credentials and offer
 * to *save* them on submit. These are connector configuration secrets (API keys,
 * client secrets, refresh tokens), not the user's login password, so we opt out.
 *
 * `data-1p-ignore` alone suppresses 1Password's inline autofill menu but NOT the
 * "save password" prompt. `data-form-type="other"` is 1Password's explicit
 * "this is not a login form" signal that suppresses the save prompt;
 * `data-lpignore="true"` is the LastPass equivalent.
 *
 * Single source of truth — spread onto every connector secret input / form /
 * container so the opt-out is identical across the wizard and the config/edit
 * modal (#483).
 */

/**
 * Password-manager "ignore this" data attributes. Spread onto a non-form
 * wrapper that groups secret inputs (e.g. the config modal's container `<div>`,
 * which has no `<form>` element). Valid on any element.
 */
export const PASSWORD_MANAGER_OPT_OUT_CONTAINER = {
  'data-1p-ignore': true,
  'data-lpignore': 'true',
  'data-form-type': 'other',
} as const;

/** Spread onto a connector secret `<input>` (e.g. `<Input {...PASSWORD_MANAGER_OPT_OUT} />`). */
export const PASSWORD_MANAGER_OPT_OUT = {
  autoComplete: 'new-password',
  ...PASSWORD_MANAGER_OPT_OUT_CONTAINER,
} as const;

/** Spread onto a connector secret `<form>` wrapper. */
export const PASSWORD_MANAGER_OPT_OUT_FORM = {
  autoComplete: 'off',
  ...PASSWORD_MANAGER_OPT_OUT_CONTAINER,
} as const;
